import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/constants";
import { toast } from "sonner";
import { Wallet, TrendingUp, Bitcoin, Megaphone, Handshake, ArrowDownRight, ArrowUpRight, Trash2, Loader2, Plus, Send, Settings, KeyRound, ShieldCheck, ExternalLink, X, Save } from "lucide-react";

const inputCls = "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid";
const SOURCES = [
  { value: "trades", label: "Crypto / Trades", icon: Bitcoin, color: "text-gold" },
  { value: "advertising", label: "Advertising", icon: Megaphone, color: "text-cyan" },
  { value: "sponsorship", label: "Sponsorship", icon: Handshake, color: "text-acid" },
  { value: "other", label: "Other", icon: TrendingUp, color: "text-muted-gray" },
];
const sourceLabel = (v) => SOURCES.find((s) => s.value === v)?.label || v;

export default function WalletPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [income, setIncome] = useState({ source: "trades", amount: "", currency: "USD", note: "" });
  const [payout, setPayout] = useState({ amount: "", note: "" });
  const [busy, setBusy] = useState("");
  const [settings, setSettings] = useState({ paypal_handle: "", payee_name: "" });
  const [pinForm, setPinForm] = useState({ current_pin: "", new_pin: "" });
  const [pinModal, setPinModal] = useState(false);
  const [pinValue, setPinValue] = useState("");

  const load = useCallback(() => {
    api.get("/wallet").then((r) => setData(r.data)).catch(() => {});
    api.get("/wallet/settings").then((r) => setSettings({ paypal_handle: r.data.paypal_handle, payee_name: r.data.payee_name })).catch(() => {});
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const payeeName = settings.payee_name || "payee";

  const addIncome = async (e) => {
    e.preventDefault();
    if (!income.amount || Number(income.amount) <= 0) return toast.error("Enter an amount");
    setBusy("income");
    try {
      await api.post("/wallet/entry", { type: "income", source: income.source, amount: Number(income.amount), currency: income.currency, note: income.note });
      setIncome({ source: income.source, amount: "", currency: "USD", note: "" });
      toast.success("Income recorded");
      load();
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  const openPayout = (e) => {
    e.preventDefault();
    if (!payout.amount || Number(payout.amount) <= 0) return toast.error("Enter an amount");
    setPinValue("");
    setPinModal(true);
  };

  const confirmPayout = async () => {
    setBusy("payout");
    try {
      await api.post("/wallet/entry", {
        type: "payout", source: "payout", amount: Number(payout.amount),
        recipient: payeeName, note: payout.note, pin: pinValue,
      });
      // open PayPal.me to complete the transfer to the payee
      if (data?.paypal_me_base) {
        window.open(`${data.paypal_me_base}/${Number(payout.amount)}`, "_blank", "noopener");
      }
      setPayout({ amount: "", note: "" });
      setPinModal(false);
      toast.success(`Payout to ${payeeName} recorded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payout failed");
    } finally { setBusy(""); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy("settings");
    try {
      const body = { paypal_handle: settings.paypal_handle, payee_name: settings.payee_name };
      if (pinForm.new_pin) { body.current_pin = pinForm.current_pin; body.new_pin = pinForm.new_pin; }
      await api.put("/wallet/settings", body);
      setPinForm({ current_pin: "", new_pin: "" });
      toast.success("Settings saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const del = async (id) => {
    await api.delete(`/wallet/entry/${id}`);
    toast.success("Entry removed");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Owner Only</div>
        <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">REVENUE WALLET</h1>
      </div>

      {/* Balance + breakdown */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1 bg-surface border border-acid/40 p-6 hard-shadow">
          <Wallet className="text-acid" size={24} />
          <div className="font-display text-5xl text-paper mt-3 leading-none" data-testid="wallet-balance">{data ? money(data.balance) : "—"}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mt-1">Available Balance</div>
          <div className="mt-4 flex gap-4 font-mono text-[11px]">
            <span className="text-acid flex items-center gap-1"><ArrowDownRight size={13} /> {data ? money(data.total_income) : "—"}</span>
            <span className="text-magenta flex items-center gap-1"><ArrowUpRight size={13} /> {data ? money(data.total_payouts) : "—"}</span>
          </div>
        </div>
        <div className="md:col-span-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {SOURCES.map((s) => (
            <div key={s.value} className="bg-surface border border-stroke p-5">
              <s.icon className={s.color} size={20} />
              <div className="font-display text-3xl text-paper mt-2 leading-none">{data ? money(data.by_source?.[s.value] || 0) : "—"}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Forms */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <form onSubmit={addIncome} className="bg-surface border border-stroke p-6 space-y-3" data-testid="income-form">
          <div className="flex items-center gap-2 font-display text-2xl text-paper"><ArrowDownRight className="text-acid" size={20} /> Record Income</div>
          <select className={inputCls} value={income.source} onChange={(e) => setIncome({ ...income, source: e.target.value })} data-testid="income-source">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="grid grid-cols-3 gap-2">
            <input className={`${inputCls} col-span-2`} type="number" step="0.01" placeholder="Amount (USD)" value={income.amount} onChange={(e) => setIncome({ ...income, amount: e.target.value })} data-testid="income-amount" />
            <input className={inputCls} placeholder="BTC?" value={income.currency} onChange={(e) => setIncome({ ...income, currency: e.target.value })} data-testid="income-currency" />
          </div>
          <input className={inputCls} placeholder="Note (e.g. ETH trade, banner deal)" value={income.note} onChange={(e) => setIncome({ ...income, note: e.target.value })} />
          <button type="submit" disabled={busy === "income"} data-testid="income-submit" className="w-full bg-acid text-ink font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-glow disabled:opacity-50 flex items-center justify-center gap-2">
            {busy === "income" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Income
          </button>
        </form>

        <form onSubmit={openPayout} className="bg-surface border border-magenta/40 p-6 space-y-3" data-testid="payout-form">
          <div className="flex items-center gap-2 font-display text-2xl text-paper"><Send className="text-magenta" size={18} /> Pay {payeeName}</div>
          <div className="font-mono text-[11px] text-muted-gray flex items-center gap-1">
            <ExternalLink size={12} /> via {data?.paypal_me_base || "paypal.me"}
          </div>
          <input className={inputCls} type="number" step="0.01" placeholder="Amount (USD)" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} data-testid="payout-amount" />
          <input className={inputCls} placeholder="Note (e.g. monthly draw, bill)" value={payout.note} onChange={(e) => setPayout({ ...payout, note: e.target.value })} />
          <button type="submit" disabled={busy === "payout"} data-testid="payout-submit" className="w-full bg-magenta text-white font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-magenta/80 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy === "payout" ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Pay with PIN
          </button>
        </form>
      </div>

      {/* Payout settings */}
      <form onSubmit={saveSettings} className="bg-surface border border-stroke p-6 mt-6" data-testid="wallet-settings-form">
        <div className="flex items-center gap-2 font-display text-2xl text-paper mb-4"><Settings className="text-cyan" size={18} /> Payout Settings</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block">Payee name</label>
            <input className={inputCls} value={settings.payee_name} data-testid="settings-payee-name"
              onChange={(e) => setSettings({ ...settings, payee_name: e.target.value })} placeholder="Kerri" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block">PayPal.me handle</label>
            <input className={inputCls} value={settings.paypal_handle} data-testid="settings-paypal-handle"
              onChange={(e) => setSettings({ ...settings, paypal_handle: e.target.value })} placeholder="KerriHoney" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block flex items-center gap-1"><ShieldCheck size={11} /> Current PIN</label>
            <input className={inputCls} type="password" value={pinForm.current_pin} data-testid="settings-current-pin"
              onChange={(e) => setPinForm({ ...pinForm, current_pin: e.target.value })} placeholder="Required to change PIN" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block">New PIN (4–8 digits)</label>
            <input className={inputCls} type="password" value={pinForm.new_pin} data-testid="settings-new-pin"
              onChange={(e) => setPinForm({ ...pinForm, new_pin: e.target.value })} placeholder="Leave blank to keep" />
          </div>
        </div>
        <button type="submit" disabled={busy === "settings"} data-testid="settings-save"
          className="mt-4 bg-cyan text-ink font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
          {busy === "settings" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
        </button>
      </form>


      {/* Ledger */}
      <h2 className="font-display text-3xl text-paper mt-10 mb-4">Ledger</h2>
      {!data ? (
        <div className="font-mono text-sm text-muted-gray animate-pulse">Loading...</div>
      ) : data.entries.length === 0 ? (
        <div className="border border-dashed border-stroke p-10 text-center font-mono text-sm text-muted-gray">No transactions yet.</div>
      ) : (
        <div className="space-y-2" data-testid="wallet-ledger">
          {data.entries.map((it) => (
            <div key={it.id} className="bg-surface border border-stroke p-4 flex items-center gap-4" data-testid="ledger-row">
              {it.type === "income" ? <ArrowDownRight className="text-acid shrink-0" size={18} /> : <ArrowUpRight className="text-magenta shrink-0" size={18} />}
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm text-paper">
                  {it.type === "income" ? sourceLabel(it.source) : `Payout → ${it.recipient || "—"}`}
                  {it.currency && it.currency !== "USD" && <span className="text-muted-gray"> · {it.currency}</span>}
                </div>
                {it.note && <div className="font-mono text-[11px] text-muted-gray truncate">{it.note}</div>}
              </div>
              <div className={`font-mono text-sm font-bold ${it.type === "income" ? "text-acid" : "text-magenta"}`}>
                {it.type === "income" ? "+" : "−"}{money(it.amount)}
              </div>
              <button onClick={() => del(it.id)} className="text-muted-gray hover:text-magenta shrink-0" data-testid={`ledger-delete-${it.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {pinModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 backdrop-blur-sm p-4" data-testid="payout-pin-modal">
          <div className="w-full max-w-sm bg-surface border border-magenta/50 p-7">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 font-display text-3xl text-paper"><KeyRound className="text-magenta" size={22} /> Enter PIN</div>
              <button onClick={() => setPinModal(false)} className="text-muted-gray hover:text-magenta"><X size={20} /></button>
            </div>
            <p className="font-mono text-xs text-muted-gray mb-4">
              Confirm payout of <span className="text-paper font-bold">{money(Number(payout.amount || 0))}</span> to {payeeName} ({data?.paypal_me_base}).
            </p>
            <input autoFocus type="password" inputMode="numeric" value={pinValue} data-testid="payout-pin-input"
              onChange={(e) => setPinValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmPayout()}
              className={`${inputCls} text-center tracking-[0.5em] text-lg`} placeholder="••••" />
            <button onClick={confirmPayout} disabled={busy === "payout"} data-testid="payout-pin-confirm"
              className="mt-4 w-full bg-magenta text-white font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-magenta/80 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy === "payout" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Confirm & Pay
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
